import { useLeaderboard } from "@/hooks/useStore";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/App";
import { Trophy, Flame, MessageSquare, GitFork, ArrowBigUp, Crown, Medal } from "lucide-react";

export function LeaderboardPage() {
	const { users, loading } = useLeaderboard();
	const { navigate } = useApp();

	if (loading) {
		return (
			<div className="flex flex-col items-center gap-4 py-20">
				<Trophy className="h-8 w-8 animate-pulse text-amber-400" />
				<p className="text-muted-foreground">Loading leaderboard...</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
					<Trophy className="h-6 w-6 text-amber-400" />
					Top Conversion Analysts
				</h1>
				<p className="text-sm text-muted-foreground">
					The sharpest minds in landing page optimization.
				</p>
			</div>

			{/* Top 3 podium */}
			{users.length >= 3 && (
				<div className="grid grid-cols-3 gap-3">
					{[users[1], users[0], users[2]].map((user, idx) => {
						const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;
						const height = rank === 1 ? "py-8" : rank === 2 ? "py-6" : "py-4";
						const medal =
							rank === 1
								? "text-amber-400 border-amber-400/30 bg-amber-400/10"
								: rank === 2
									? "text-zinc-300 border-zinc-300/30 bg-zinc-300/10"
									: "text-orange-600 border-orange-600/30 bg-orange-600/10";

						return (
							<Card
								key={user.id}
								className={`cursor-pointer border-border/40 bg-card/50 transition-all hover:border-border/80 ${rank === 1 ? "ring-1 ring-amber-400/20" : ""}`}
								onClick={() => navigate({ name: "profile", id: user.id })}
							>
								<CardContent className={`flex flex-col items-center ${height}`}>
									<div className={`mb-2 flex h-10 w-10 items-center justify-center rounded-full border ${medal}`}>
										{rank === 1 ? <Crown className="h-5 w-5" /> : <Medal className="h-5 w-5" />}
									</div>
									<div className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/20 text-lg font-bold text-orange-500">
										{user.username[0].toUpperCase()}
									</div>
									<p className="mt-1 text-sm font-semibold">{user.username}</p>
									<p className="text-lg font-bold text-orange-400">{user.reputation.toLocaleString()}</p>
									<p className="text-xs text-muted-foreground">reputation</p>
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			{/* Full ranking */}
			<div className="space-y-2">
				{users.map((user, idx) => (
					<Card
						key={user.id}
						className="cursor-pointer border-border/40 bg-card/50 transition-all hover:border-border/80 hover:bg-card/80"
						onClick={() => navigate({ name: "profile", id: user.id })}
					>
						<CardContent className="flex items-center gap-4 p-4">
							<span className="w-8 text-center text-lg font-bold text-muted-foreground">
								#{idx + 1}
							</span>

							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/20 text-sm font-bold text-orange-500">
								{user.username[0].toUpperCase()}
							</div>

							<div className="min-w-0 flex-1">
								<p className="font-semibold">{user.username}</p>
								<p className="truncate text-xs text-muted-foreground">{user.bio || "No bio"}</p>
							</div>

							<div className="flex items-center gap-4 text-xs text-muted-foreground">
								<span className="flex items-center gap-1" title="Roasts submitted">
									<Flame className="h-3.5 w-3.5 text-orange-500" />
									{user.roastCount}
								</span>
								<span className="flex items-center gap-1" title="Comments">
									<MessageSquare className="h-3.5 w-3.5" />
									{user.commentCount}
								</span>
								<span className="flex items-center gap-1" title="Rewrites">
									<GitFork className="h-3.5 w-3.5" />
									{user.rewriteCount}
								</span>
								<span className="flex items-center gap-1" title="Upvotes received">
									<ArrowBigUp className="h-3.5 w-3.5" />
									{user.upvotesReceived}
								</span>
							</div>

							<Badge
								variant="outline"
								className="ml-2 border-orange-500/20 bg-orange-500/10 text-orange-400"
							>
								{user.reputation.toLocaleString()} pts
							</Badge>
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}
